import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts'
import styled, { ThemeContext } from 'styled-components'
import dayjs from 'dayjs'
import { capitalizeFirstLetter, formatNumber } from '~/utils/helpers'

interface MilkTypeLineChartProps {
  data: Array<{
    date: string
    price: string | number
    fat: string | number
    protein: string | number
  }>
}

const ChartWrapper = styled.div`
  width: 100%;
  height: 400px;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const TooltipWrapper = styled.div`
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .tooltipTitle {
    margin: 0;
    padding: 2px 0;
    font-size: 14px;
  }
`

const MilkTypeLineChart: React.FC<MilkTypeLineChartProps> = ({ data }) => {
  const { t } = useTranslation()
  const { firstColor } = useContext(ThemeContext)

  const chartData = data.map((item) => {
    const price = typeof item.price === 'string' ? item.price.replace(',', '.') : item.price
    const fat = typeof item.fat === 'string' ? item.fat.replace(',', '.') : item.fat
    const protein = typeof item.protein === 'string' ? item.protein.replace(',', '.') : item.protein

    return {
      month: capitalizeFirstLetter(dayjs(item.date).format('MMM YYYY')),
      price: price ? Number(price) : null,
      fat: fat ? Number(fat) : null,
      protein: protein ? Number(protein) : null
    }
  })

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <TooltipWrapper>
          <div style={{ padding: 5 }}>
            <p className='tooltipTitle' style={{ color: 'black', fontWeight: 'bold' }}>
              {label}
            </p>
          </div>
          {payload.map((item: any) => {
            if (item.value !== null) {
              return (
                <div style={{ padding: 5 }} key={item.name}>
                  <p className='tooltipTitle' style={{ color: item.color }}>
                    {`${item.name}: ${formatNumber(item.value, 2)} ${item.unit || ''}`}
                  </p>
                </div>
              )
            }
            return null
          })}
        </TooltipWrapper>
      )
    }
    return null
  }

  return (
    <ChartWrapper>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis
            dataKey='month'
            angle={-45}
            textAnchor='end'
            height={80}
            interval={0}
          />
          <YAxis
            yAxisId='left'
            label={{
              value: t('Price (€ Ct/kg)'),
              angle: -90,
              position: 'insideLeft'
            }}
          />
          <YAxis
            yAxisId='right'
            orientation='right'
            label={{
              value: t('Fat & Protein (%)'),
              angle: 90,
              position: 'insideRight'
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            yAxisId='left'
            type='monotone'
            dataKey='price'
            name={t('Price (€ Ct/kg)')}
            stroke={firstColor}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            connectNulls
          />
          <Line
            yAxisId='right'
            type='monotone'
            dataKey='fat'
            name={t('Fat (in %)')}
            stroke='#82ca9d'
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            connectNulls
          />
          <Line
            yAxisId='right'
            type='monotone'
            dataKey='protein'
            name={t('Protein (in %)')}
            stroke='#8884d8'
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}

export default MilkTypeLineChart
